import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES, PROJECTS } from "@/lib/site";

const SOLUTIONS = [
  {
    title: "Closets & Storage",
    desc: "Walk-in closets, reach-ins, and wardrobe systems designed around what you own — every shelf, drawer, and rail with a purpose.",
    img: PROJECTS[5].img,
    alt: "Custom walk-in closet storage solution",
  },
  {
    title: "Built-Ins",
    desc: "Media walls, shelving, window seats, and cabinetry that looks like it was always part of your home — because it was made for it.",
    img: PROJECTS[6].img,
    alt: "Custom built-in shelving in a living room",
  },
  {
    title: "Full Home Interiors",
    desc: "Complete interior transformations that bring thoughtful design, quality finishes, and professional workmanship to every room.",
    img: PROJECTS[7].img,
    alt: "Complete home interior renovation",
  },
];

export default function Interiors() {
  return (
    <main data-testid="interiors-page">
      <SEO
        title="Home Interior Renovations, Closets & Built-Ins GTA | Hive Interiors"
        description="Custom interior solutions across the GTA: closets, built-ins, storage solutions and complete home interior renovations designed around your lifestyle."
      />
      <PageHero
        image={IMAGES.interiorHero}
        overline="Interiors"
        title="Custom Interior"
        italic="Solutions"
        sub="Vanities, closets, built-ins, storage solutions, and complete home interior renovations — customized interior projects of every scale."
        alt="Custom home interior renovation in the GTA"
      />

      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
            <Reveal className="md:col-span-3">
              <Overline>01 / Beyond the Kitchen</Overline>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-9">
              <h2 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08] max-w-3xl">
                Every Room, <span className="italic text-primary">Considered</span>
              </h2>
              <p className="mt-6 max-w-2xl text-base text-muted-foreground leading-relaxed">
                The same design thinking and craftsmanship we bring to kitchens applies to your whole home. If a space in your house isn't working, we can design a custom solution that does.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div data-testid={`interiors-solution-${i + 1}`} className="group bg-card border border-border">
                  <div className="overflow-hidden aspect-[4/5]">
                    <img
                      src={s.img}
                      alt={s.alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="font-heading text-2xl tracking-tight">{s.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-16 flex justify-center">
              <Link
                to="/contact"
                data-testid="interiors-consultation-button"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground group"
              >
                Discuss Your Project
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Have a Space That"
        italic="Isn't Working?"
        text="From closets to built-ins to full home interiors, share your ideas and we'll design a custom solution around your space and lifestyle."
      />
    </main>
  );
}
